<template>
    <div>
        <b-modal
            id="appointmentModal"
            ref="appointmentModal"
            :lazy="true"
            @hidden="close"
            :scrollable="enableScrollable"
            :no-close-on-backdrop="true"
            :title="title"
            title-tag="h2"
        >
            <!-- Modal Form -->
            <transition name="slide-fade">
                <appointment
                    ref="appointment"
                    v-if="page == 'appointment'"
                    :data="appointment_data"
                    @loading="loading = true"
                    @loaded="loading = false"
                    @update-slot="updateTimeSlot"
                    @update-timeslot="updateTimeSlotFlag"
                    @resetManualCheckEndLTEStart="
                        () => {
                            this.manualCheckEndLTEStart = false;
                        }
                    "
                />
                <client
                    modal-id="appointmentModal"
                    ref="client"
                    v-if="page == 'client'"
                    :data="client_data"
                    :group_insert="false"
                />
                <confirmation
                    ref="confirmation"
                    v-if="page == 'confirmation'"
                    :data="confirm_data"
                    @loading="loading = true"
                    @loaded="loading = false"
                />
            </transition>
            <div slot="modal-footer" class="w-100">
                <b-button
                    v-if="!loading"
                    @click="$refs.appointmentModal.hide()"
                    class="float-left"
                    variant="secondary"
                    >Cancel</b-button
                >
                <div v-if="loading" class="float-right">
                    <img src="assets/ajax-loader.gif" />
                </div>
                <div v-else class="float-right">
                    <b-button
                        @click="back"
                        v-if="page != 'appointment'"
                        variant="primary"
                        >Back</b-button
                    >&nbsp;
                    <a
                        v-if="endDateLTEStartDate || manualCheckEndLTEStart"
                        @click.prevent
                        href="#"
                        class="mr-2"
                        v-b-tooltip.hover.topleft
                        title="The end date-time of the appointment (To) must be greater than the start time (From)."
                    >
                        Error (?)
                    </a>
                    <b-button
                        @click="save"
                        v-if="page == 'confirmation' || showSave"
                        variant="primary"
                        :disabled="
                            endDateLTEStartDate || manualCheckEndLTEStart
                        "
                        >Save</b-button
                    >
                    <b-button
                        @click="next"
                        v-else
                        variant="primary"
                        :disabled="
                            endDateLTEStartDate ||
                            manualCheckEndLTEStart ||
                            emptyTimeSlot()
                        "
                    >
                        Next
                    </b-button>
                </div>
            </div>
        </b-modal>
        <notifications group="appointment">
            <template slot="body" slot-scope="props">
                <div class="vue-notification-template vue-notification">
                    <div class="notification-title">
                        {{ props.item.title }}
                        <a @click="props.close" class="close">&times;</a>
                    </div>
                    <div class="notification-content">
                        <ol>
                            <li
                                v-for="client in notification.client"
                                :key="client.id"
                            >
                                {{ client.content }}
                            </li>
                            <li
                                v-for="patient in notification.patient"
                                :key="patient.id"
                            >
                                {{ patient.content }}
                            </li>
                        </ol>
                    </div>
                </div>
            </template>
        </notifications>
    </div>
</template>
<style></style>

<script>
import Appointment from "./appointment.vue";
import Client from "@/module/commons/client/form.vue";
import Confirmation from "./confirmation.vue";
import ScheduleHelper from "@/module/schedule/helper";
import uuidv4 from "uuid/v4";
import CensusMixin from "@/mixins/census";

export default {
    name: "CreateUpdateAppointment",
    components: {
        Appointment,
        Confirmation,
        Client
    },
    mixins: [ScheduleHelper, CensusMixin],
    props: {
        instanceId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            loading: true,
            page: "appointment",
            dialogId: null,
            appointment_data: {
                id: null,
                start: null,
                end: null,
                client: null,
                client_id: null,
                reason: "",
                patient: null,
                patient_id: null,
                location: null,
                note: "",
                referrer: null,
                staff: null,
                staff_id: null,
                is_staff: 0,
                type: 1,
                repeat: 1,
                stop: 1,
                appointment_type: null,
                appointment_type_name: "",
                save_event_type: 1,
                referral_id: null,
                client_changed: 0,
                patient_changed: 0,
                time_changed: false,
                original_repeat: 1,
                occurrence: 1,
                specific_interval: 1,
                repeat_interval: 1,
                send_pre_visit: false,
                pre_visit_send: false,
                pre_visit_send_at: null
            },
            client_data: {
                reminders: [],
                reminder_options: [],
                home_country_code: null,
                mobile_country_code: null,
                fax_country_code: null,
                work_country_code: null,
                address: null,
                city: null,
                state: null,
                zipcode: null,
                country: null
            },
            confirm_data: {
                confirmation_option: "NO",
                confirmation_message: "",
                reminder_option: 0,
                reminder_message: "",
                time_period_id: 4,
                reminder_send: 2,
                reminder_send_option_id: 1,
                reminder_type_id: 0,
                due: 0,
                message: "",
                send_pre_visit: false
            },
            notification: {
                client: [],
                patient: []
            },
            manualCheckEndLTEStart: false
        };
    },
    computed: {
        enableScrollable() {
            let r = this.appointment_data.module_referrer;

            if (
                (r === "patient-profile" || r === "client-profile") &&
                this.page === "appointment"
            ) {
                return false;
            }

            return true;
        },
        endDateLTEStartDate() {
            // End Less-than-or-equal to Start
            // end datetime should be greater than start
            const { start, end } = this.appointment_data;
            return this.checkEndLTEStart(start, end);
        },
        title() {
            switch (this.page) {
                case "appointment":
                    return !this.appointment_data.id
                        ? "New Appointment"
                        : this.appointment_data.occurrence_id
                        ? "Edit Recurring Appointment"
                        : "Edit Appointment";
                case "client":
                    return "Client Information";
                case "confirmation":
                    return "Confirmation";
                default:
                    break;
            }
            return "";
        },
        hasNotification() {
            return (
                this.notification.patient.length ||
                this.notification.client.length
            );
        },
        showSave() {
            // new
            let save = true;
            if (!this.appointment_data.id && this.appointment_data.client) {
                save = false;
            }

            // existing appointment
            if (
                this.appointment_data.id &&
                this.appointment_data.time_changed &&
                this.appointment_data.client
            ) {
                save = false;
            }

            //hide save button if there are changes to client/patient
            if (
                this.appointment_data.client_changed == 1 ||
                this.appointment_data.patient_changed == 1
            ) {
                save = false;

                if (!this.appointment_data.client) {
                    save = true;
                }
            }
            return save;
        },
        isClientNoOwner() {
            return this.appointment_data?.client?.index === 1;
        }
    },
    watch: {
        "appointment_data.client_id"(value, oldValue) {
            this.notification.client = [];
            if (value) {
                this.clientNotes(value);

                let r = this.appointment_data.module_referrer;
                if (r === "patient-profile" || r === "client-profile") {
                    this.setClientInfo(value);
                }
            }
        },
        "appointment_data.patient_id"(newValue, oldValue) {
            this.notification.patient = [];
            if (newValue && !oldValue && this.appointment_data.note) {
                this.appointment_data.note += "\n";
            } else if (newValue && oldValue) {
                this.appointment_data.note = "";
            }
            if (newValue && this.appointment_data.patient_changed) {
                this.patientNotes(newValue);
            }
        },
        hasNotification(value) {
            if (value) {
                this.popupNotification();
            } else {
                this.$notify({
                    group: "appointment",
                    clean: true
                });
            }
        }
    },
    created() {
        window.onbeforeunload = () => {
            this.$socket.client.emit("v-broadcast-all", {
                id: this.dialogId,
                action: "draft-remove",
                businessId: this.$root.setting.business_id
            });
        };
    },
    methods: {
        checkEndLTEStart(start, end) {
            // Move to a function, so that can be called before proceed saving to make sure issue 1 and 2 in VET-10601 no longer producable, the root cause of the issues are not found but this is last check before proceed save and next
            const endLTEStart = end <= start;
            if (endLTEStart) {
                this.manualCheckEndLTEStart = true;
                return true;
            }
            this.manualCheckEndLTEStart = false;
            return false;
        },
        emptyTimeSlot() {
            let { module_referrer, hasTimeSlot } = this.appointment_data;
            let modules = ["patient-profile", "client-profile"];

            if (modules.includes(module_referrer)) {
                if (hasTimeSlot) {
                    return false; // if has time slot, means it is not empty
                } else {
                    return true;
                }
            }

            return false;
        },
        updateTimeSlot(obj = {}) {
            this.appointment_data = {
                ...this.appointment_data,
                ...obj
            };

            this.$socket.client.emit("v-broadcast-all", {
                action: "draft-add",
                id: uuidv4(),
                title:
                    "Tentatively blocked by " +
                    this.$root.currentActor.firstname +
                    " " +
                    this.$root.currentActor.lastname,
                start: this.appointment_data.start,
                end: this.appointment_data.end,
                businessId: this.$root.setting.business_id,
                module: "patient-profile"
            });
        },
        updateTimeSlotFlag(value) {
            this.appointment_data.hasTimeSlot = value;
        },
        popupNotification() {
            this.$notify({
                group: "appointment",
                clean: true
            });
            if (
                this.notification.client.length ||
                this.notification.patient.length
            ) {
                this.$notify({
                    group: "appointment",
                    title: "ATTENTION",
                    duration: -1,
                    closeOnClick: false
                });
            }
        },
        open(params) {
            this.page = null;
            this.dialogId = params.id;

            this.$nextTick(() => {
                _.assign(
                    this.$data.appointment_data,
                    this.$options.data().appointment_data
                );
                _.assign(
                    this.$data.client_data,
                    this.$options.data().client_data
                );

                if (_.isObject(params) && params.is_new) {
                    // -- create pgae
                    this.appointment_data.staff_id = params.resource
                        ? params.resource.id
                        : 0;
                    this.appointment_data.start = this._toDateTime(
                        params.start
                    );
                    // this.appointment_data.end = moment(params.end).tz(
                    //     this._timeZone
                    // );
                    this.$refs.appointmentModal.show();
                    this.page = "appointment";

                    // based on referrer (module referrer)
                    // from patient profile
                    if (params.referrer === "patient-profile") {
                        this.appointment_data.module_referrer = params.referrer;
                        this.appointment_data.patient = params.patient;
                        this.appointment_data.patient_id = params.patient.id;

                        // if has owner(s)
                        if (
                            params.patient.active_owners &&
                            params.patient.active_owners[0]
                        ) {
                            let owner = params.patient.active_owners[0]; // use first owner as client
                            this.appointment_data.client_id = owner.id;
                        }
                    }
                    // from client profile
                    else if (params.referrer === "client-profile") {
                        this.appointment_data.module_referrer = params.referrer;
                        this.appointment_data.client_id = params.client.id;
                    }
                } else if (_.isObject(params) && !!params.is_duplicate) {
                    // -- Duplicate
                    this.getAppointmentData(params.id).then(() => {
                        this.appointment_data = _.omit(this.appointment_data, [
                            "id",
                            "invoice",
                            "invoice_id",
                            "estimate",
                            "estimate_id",
                            "soap",
                            "soap_id",
                            "occurrence_id",
                            "cancellation_date",
                            "cancellation_note",
                            "cancellation_reason",
                            "status"
                        ]);
                        this.page = "appointment";
                        this.$refs.appointmentModal.show();
                    });
                } else {
                    // -- Edit existing
                    this.getAppointmentData(params).then(() => {
                        this.page = "appointment";
                        this.$refs.appointmentModal.show();
                    });
                }

                if (this.$refs.appointment) {
                    _.assign(
                        this.$refs.appointment.$data,
                        this.$refs.appointment.$options.data()
                    );
                    this.$refs.appointment.$validator.reset();
                }
            });
        },
        /**
         * get client/owner info
         * and assign it to client object as per Schedule.
         * this function will be used only for patient and client profile.
         * we have to follow schedule version of client object, in order
         * to make the `Reminder` vue-select working properly as in Schedule.
         */
        async setClientInfo(clientId) {
            let res = await this.$http.get(
                `${process.env.BARRAMUNDI}actor/client/${clientId}`
            );

            if (res.data && res.data.response && res.data.response.resource) {
                let client = res.data.response.resource;
                let fullName = `${client.firstname} ${client.lastname}`;

                this.appointment_data.client = {
                    id: client.id,
                    index: client.index,
                    encoded_id: client.encoded_id,
                    firstname: client.firstname,
                    lastname: client.lastname,
                    fullName: fullName,
                    label: fullName,
                    displayName: client.displayName,
                    initial: client.initial,
                    email: client.email,
                    address: client.address,
                    city: client.city,
                    state: client.state,
                    country: client.country,
                    zipcode: client.zipcode,
                    home: client.home,
                    home_country_code: client.home_country_code,
                    work: client.work,
                    work_country_code: client.work_country_code,
                    mobile: client.mobile,
                    mobile_country_code: client.mobile_country_code,
                    fax: client.fax,
                    fax_country_code: client.fax_country_code,
                    reminderOptions: client.reminderOptions,
                    reminder_options: client.reminderOptions,
                    birthdate: client.birthdate,
                    driver_license: client.driver_license,
                    actor_status_id: client.status.id,
                    statusClient: client.status // keep status object, but not using `status` prop
                };
            }
        },
        patientNotes(id) {
            let self = this;
            return self.$http
                .get(process.env.BARRACUDA + "patient/" + id + "/notes")
                .then(function (response) {
                    self.notification.patient = _.filter(
                        response.data.response.resource,
                        { notification: 1 }
                    );

                    /**
                     * only add patient notes if new appointment
                     * so, check if appointment id exist
                     */
                    if (response.data.response.resource.length > 0) {
                        self.appointment_data.note += "Precautions: ";
                        _.forEach(
                            response.data.response.resource,
                            (element, key) => {
                                self.appointment_data.note +=
                                    "(" +
                                    parseInt(key + 1) +
                                    ") " +
                                    element.content +
                                    " ";
                            }
                        );
                    }
                });
        },
        clientNotes(id) {
            let self = this;
            return self.$http
                .get(
                    process.env.ACTOR_SERVICE + "client/" + id + "/popup-notes"
                )
                .then(function (response) {
                    self.notification.client = response.data.response.resource;
                });
        },
        async save() {
            try {
                let isValid = false;
                this.loading = true;

                if (this.page === "appointment") {
                    isValid = await this.$refs.appointment.$validator.validateAll();
                } else if (this.page === "confirmation") {
                    isValid = await this.$refs.confirmation.$validator.validateAll();
                }

                if (!isValid) {
                    throw new Error("Invalid form");
                }

                let post = _.cloneDeep(this.appointment_data);

                if (this.checkEndLTEStart(post.start, post.end)) {
                    // VET-10601 (Issue 1&2)
                    return;
                }

                post.end = post.end.toSeconds();
                post.start = post.start.toSeconds();

                // module referrer
                let moduleRef = post.module_referrer || null;

                // -- location
                post.location_id = this._parseLocation(post.location);

                // -- Client add reminder_option
                post.client = _(post.client)
                    .merge({ reminder_options: [] })
                    .omit(
                        "displayName",
                        "fullName",
                        "label",
                        "confirmation",
                        "reminder",
                        "status"
                    ) // Cleanup so we dont send unnesasary properties
                    .value();

                //reset stop to null if no repeat is set
                if (post.repeat == 1) {
                    post.stop = null;
                }

                if (post.original_repeat == 1 && post.repeat > 1) {
                    post.save_event_type = 3;
                }

                if (this.page == "confirmation") {
                    let cdata = this.confirm_data;

                    post.reminder = {
                        reminder_send: cdata.reminder_send,
                        time_period_id: cdata.time_period_id,
                        reminder_send_option_id: cdata.reminder_send_option_id,
                        reminder_type_id: cdata.reminder_option,
                        sms_message: cdata.reminder_message,
                        message: cdata.reminder_message
                    };

                    post.confirmation = {
                        confirmation_method: cdata.confirmation_option,
                        confirmation_subject: "",
                        confirmation_message: cdata.confirmation_message
                    };

                    post.send_pre_visit = cdata.send_pre_visit;
                }

                let isEditAppointment = this.appointment_data.id ? true : false;
                let uri;
                let response;

                if (isEditAppointment) {
                    uri = `${process.env.SCHEDULE_SERVICE}appointment/${this.appointment_data.id}`;
                    response = await this.$http.put(uri, post);
                } else {
                    uri = `${process.env.SCHEDULE_SERVICE}appointment`;
                    response = await this.$http.post(uri, post);
                }

                this.$refs.appointmentModal.hide();
                this._message(response.data.response.message);

                if (
                    moduleRef === "patient-profile" ||
                    moduleRef === "client-profile"
                ) {
                    // show result on schedule instantly -- if new appointment
                    if (!isEditAppointment) {
                        this.$socket.client.emit("v-broadcast-all", {
                            action: "refresh",
                            businessId: this._setting.business_id,
                            params: this.appointment_data
                        });
                    }

                    this.$emit("reload");
                } else {
                    this.$parent.refetch();
                }

                // Emit socket to refresh census
                const payload = {
                    appointmentDate: this.appointment_data?.start.toSeconds(),
                    appointmentId: this.appointment_data?.id,
                    instanceId: this.instanceId
                };

                this.refreshCensus(payload);
            } catch (err) {
                this.loading = false;
            }
        },
        getAppointmentData(id) {
            let self = this;
            return this.$http
                .get(process.env.SCHEDULE_SERVICE + "appointment/" + id)
                .then(function (response) {
                    self.appointment_data = _.merge(
                        self.appointment_data,
                        response.data.response.resources
                    );

                    self.original_patient_id = _.get(
                        self,
                        "appointment_data.patient.id",
                        null
                    );

                    self.appointment_data.start = self._toDateTime(
                        self.appointment_data.start
                    );

                    self.appointment_data.end = self._toDateTime(
                        self.appointment_data.end
                    );

                    self.appointment_data.referrer = self._parseReferrer(
                        self.appointment_data.referral,
                        self.appointment_data.referralSubject
                    );

                    if (self.appointment_data.referrer) {
                        self.appointment_data.referral_id = self._bindReferrer(
                            self.appointment_data.referrer
                        );
                    }

                    if (!self.appointment_data.stop) {
                        self.appointment_data.stop = 1;
                    }

                    self.appointment_data.original_repeat =
                        self.appointment_data.repeat;
                    return self.appointment_data;
                });
        },
        next() {
            let self = this;
            if (self.page == "appointment") {
                self.$refs.appointment.$validator.validateAll().then(result => {
                    if (result) {
                        if (
                            self.checkEndLTEStart(
                                self.appointment_data.start,
                                self.appointment_data.end
                            )
                        ) {
                            // VET-10601 (Issue 1&2)
                            return;
                        }

                        self.client_data = self.appointment_data.client;
                        self.confirm_data = _.merge(
                            self.confirm_data,
                            self.appointment_data
                        );
                        self.confirm_data.client = self.appointment_data.client;
                        self.confirm_data.patient =
                            self.appointment_data.patient;
                        if (
                            !self.isClientNoOwner &&
                            self.appointment_data.client &&
                            !self.appointment_data.client.new &&
                            (!self.appointment_data.id || // I am old appointment but also my client was changed
                                self.appointment_data.client_changed)
                        ) {
                            self.page = "client";
                        } else {
                            self.page = "confirmation";
                        }
                    }
                });
            } else if (self.page == "client") {
                self.$refs.client.$validator.validateAll().then(result => {
                    if (result) {
                        self.client_data.reminder_options = self.$refs.client.remindersToString();
                        self.page = "confirmation";
                        self.confirm_data.client = self.client_data;
                        self.confirm_data.appointment_type_name =
                            self.appointment_data.appointment_type_name;
                        self.confirm_data.staff = self.appointment_data.staff;
                    }
                });
            }
        },
        back() {
            if (this.page == "confirmation") {
                if (
                    !this.isClientNoOwner &&
                    this.appointment_data.client &&
                    !this.appointment_data.client.new &&
                    (!this.appointment_data.id || // I am old appointment but also my client was changed
                        this.appointment_data.client_changed)
                ) {
                    this.page = "client";
                } else {
                    this.page = "appointment";
                }
            } else if (this.page == "client") {
                this.page = "appointment";
            }
        },
        close() {
            if (!this.appointment_data.id) {
                this.$socket.client.emit("v-broadcast-all", {
                    id: this.dialogId,
                    action: "draft-remove",
                    businessId: this.$root.setting.business_id
                });
            }
        }
    }
};
</script>
